import { useKeyboardControls } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'

import { markLevelComplete } from '@config'
import {
  applyInputPhysics,
  driveFromEntrance,
  driveNPCsToExit,
  driveToExit,
  hasClearPathToExit,
  isConsistComplete,
  isLocoOnExitCell,
  setNPCSpeed,
  stepSimulation,
} from '@engine'
import { GamePhase, useGameStore } from '@stores'
import { useLevelControls } from '@utils'

const SLOW_DOWN_AFTER = 2.5
const MAX_FRAME_DELTA = 0.05
const MAX_SIMULATION_STEP = 1 / 120
const STEP_EPSILON = 1e-8

function runSimulationSteps(delta: number, step: (dt: number) => void) {
  let remaining = Math.min(delta, MAX_FRAME_DELTA)

  while (remaining > STEP_EPSILON) {
    const dt = Math.min(remaining, MAX_SIMULATION_STEP)
    step(dt)
    remaining -= dt
  }
}

function TitleLoop() {
  const elapsed = useRef(0)
  const slowedDown = useRef(false)

  useFrame((_, delta) => {
    const { network, phase, levelComplete } = useGameStore.getState()
    if (phase < GamePhase.TITLE || phase > GamePhase.LEVEL_SELECTION_TRANSITION) return

    if (phase === GamePhase.TITLE && !slowedDown.current) {
      elapsed.current += Math.min(delta, 1 / 30)
      if (elapsed.current >= SLOW_DOWN_AFTER) {
        network.npcs.forEach(npc => setNPCSpeed(network, npc.id, 1))
        slowedDown.current = true
      }
    }

    if (phase === GamePhase.LEVEL_SELECTION && !levelComplete) {
      network.npcs.forEach(npc => setNPCSpeed(network, npc.id, 0))
    }

    if (levelComplete) driveNPCsToExit(network)
    runSimulationSteps(delta, dt => stepSimulation(network, dt))
  })

  return null
}

function LevelTransitionLoop() {
  useFrame((_, delta) => {
    const { network, phase } = useGameStore.getState()
    if (phase !== GamePhase.LEVEL_TRANSITION) return

    runSimulationSteps(delta, dt => {
      const escapeInput = driveToExit(network)
      if (escapeInput) applyInputPhysics(network, dt, escapeInput.forward, escapeInput.backward)
      stepSimulation(network, dt)
    })
  })

  return null
}

function NoGoalLoop() {
  const [, getKeys] = useKeyboardControls()

  const setLevelComplete = useGameStore(state => state.setLevelComplete)

  const wasComplete = useRef(false)

  useFrame((_, delta) => {
    const { network, phase, level, currentLevelIndex } = useGameStore.getState()
    const goal = level.goal.sequence || []
    if (phase !== GamePhase.PLAYING || goal.length > 0) return

    const loco = network.locomotive
    const newLevelComplete = loco?.edgeId.startsWith('ESCAPE:') ?? false

    if (newLevelComplete && !wasComplete.current) {
      wasComplete.current = true
      setLevelComplete(true)
      markLevelComplete(currentLevelIndex)
    }
    if (!newLevelComplete && wasComplete.current) {
      wasComplete.current = false
      setLevelComplete(false)
    }

    runSimulationSteps(delta, dt => {
      let { forward, backward } = getKeys()

      const currentLoco = network.locomotive
      const isOnExitCell = isLocoOnExitCell(network, currentLoco)

      const entranceInput = driveFromEntrance(network)
      if (entranceInput) {
        forward = entranceInput.forward
        backward = entranceInput.backward
      } else if (isOnExitCell) {
        const escapeInput = driveToExit(network)
        if (escapeInput) {
          forward = escapeInput.forward
          backward = escapeInput.backward
        }
      }

      applyInputPhysics(network, dt, forward, backward)
      stepSimulation(network, dt)
    })
  })

  return null
}

function GoalLoop() {
  const [, getKeys] = useKeyboardControls()

  const setLevelComplete = useGameStore(state => state.setLevelComplete)

  const wasComplete = useRef(false)
  const levelCompleteTimer = useRef<number | null>(null)
  const cachedConsistResult = useRef(false)
  const LEVEL_COMPLETE_DELAY = 0.5

  useFrame((_, delta) => {
    const { network, phase, level, currentLevelIndex } = useGameStore.getState()
    const goal = level.goal.sequence || []
    if (phase !== GamePhase.PLAYING || goal.length === 0) return

    if (network.consistDirty) {
      cachedConsistResult.current = isConsistComplete(network, goal)
      network.consistDirty = false
    }

    const clearPath = hasClearPathToExit(network)
    const canComplete = cachedConsistResult.current && clearPath

    if (canComplete && !wasComplete.current) {
      wasComplete.current = true
      levelCompleteTimer.current = 0
    }

    if (!canComplete && wasComplete.current) {
      wasComplete.current = false
      levelCompleteTimer.current = null
      setLevelComplete(false)
    }

    if (levelCompleteTimer.current !== null && levelCompleteTimer.current < LEVEL_COMPLETE_DELAY) {
      levelCompleteTimer.current += delta
      if (levelCompleteTimer.current >= LEVEL_COMPLETE_DELAY) {
        setLevelComplete(true)
        markLevelComplete(currentLevelIndex)
      }
    }

    runSimulationSteps(delta, dt => {
      let { forward, backward } = getKeys()

      const entranceInput = driveFromEntrance(network)
      if (entranceInput) {
        forward = entranceInput.forward
        backward = entranceInput.backward
      } else if (canComplete) {
        const escapeInput = driveToExit(network)
        if (escapeInput) {
          forward = escapeInput.forward
          backward = escapeInput.backward
        }
      }

      applyInputPhysics(network, dt, forward, backward)
      stepSimulation(network, dt)
    })
  })

  return null
}

export function GameLoop() {
  const { paused: debugPaused } = useLevelControls()
  const paused = useGameStore(state => state.paused)

  if (debugPaused || paused) return null

  return (
    <>
      <TitleLoop />
      <LevelTransitionLoop />
      <NoGoalLoop />
      <GoalLoop />
    </>
  )
}
